import { useState, useEffect, useLayoutEffect, useCallback, useRef, memo } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Receipt,
  Plug,
  Loader2,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  RotateCcw,
  Globe,
  Building2,
  FileText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function resolveStrutturaScopeId(struttura, orgStrutture = []) {
  if (!struttura) return null;
  const sid = struttura.id || struttura.zohoRecordId || null;
  if (!sid) return null;
  const match = orgStrutture.find(
    (s) => s.id === sid || s.zohoRecordId === sid || s.id === struttura.id || s.zohoRecordId === struttura.zohoRecordId
  );
  return match?.id || match?.zohoRecordId || sid;
}

const FATT_PROVIDER_DEFAULTS = {
  abilitato: false,
  provider: "",
  api_key: "",
  api_secret: "",
  api_uid: "",
  aruba_credential: "",
  aruba_domain: "",
};

const FATT_DATI_DEFAULTS = {
  ragione_sociale: "",
  partita_iva: "",
  codice_fiscale: "",
  indirizzo: "",
  cap: "",
  citta: "",
  provincia: "",
  nazione: "IT",
  regime_fiscale: "RF01",
};

const FATT_RECAPITI_DEFAULTS = {
  pec: "",
  codice_sdi: "",
  iban: "",
  banca: "",
};

const FATT_IVA_DEFAULTS = {
  regime_iva_default_retta: "esente_art10",
  regime_iva_default_servizi: "iva_22",
  regime_iva_default_rimborsi: "esclusa_art15",
  numerazione_prefix: "FT",
  numerazione_prefix_note_credito: "NC",
};

function splitConfigFromApi(cfg) {
  return {
    provider: {
      ...FATT_PROVIDER_DEFAULTS,
      abilitato: !!cfg.abilitato,
      provider: cfg.provider || "",
      api_key: cfg.api_key || "",
      api_secret: cfg.api_secret || "",
      api_uid: cfg.api_uid || "",
      aruba_credential: cfg.aruba_credential || "",
      aruba_domain: cfg.aruba_domain || "",
    },
    dati: {
      ...FATT_DATI_DEFAULTS,
      ragione_sociale: cfg.ragione_sociale || "",
      partita_iva: cfg.partita_iva || "",
      codice_fiscale: cfg.codice_fiscale || "",
      indirizzo: cfg.indirizzo || "",
      cap: cfg.cap || "",
      citta: cfg.citta || "",
      provincia: cfg.provincia || "",
      nazione: cfg.nazione || "IT",
      regime_fiscale: cfg.regime_fiscale || "RF01",
    },
    recapiti: {
      ...FATT_RECAPITI_DEFAULTS,
      pec: cfg.pec || "",
      codice_sdi: cfg.codice_sdi || "",
      iban: cfg.iban || "",
      banca: cfg.banca || "",
    },
    iva: {
      ...FATT_IVA_DEFAULTS,
      regime_iva_default_retta: cfg.regime_iva_default_retta || "esente_art10",
      regime_iva_default_servizi: cfg.regime_iva_default_servizi || "iva_22",
      regime_iva_default_rimborsi: cfg.regime_iva_default_rimborsi || "esclusa_art15",
      numerazione_prefix: cfg.numerazione_prefix || "FT",
      numerazione_prefix_note_credito: cfg.numerazione_prefix_note_credito || "NC",
    },
  };
}

/** Provider + credenziali: stato isolato → digitazione non ricalcola altre sezioni (molti Select). */
const FattProviderFields = memo(function FattProviderFields({ value, setValue, onTest, testing }) {
  const patch = (p) => setValue((prev) => ({ ...prev, ...p }));
  return (
    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
      <div className="flex items-center justify-between">
        <div>
          <Label className="font-medium">Abilita Fatturazione Elettronica</Label>
          <p className="text-xs text-slate-500">Attiva l'invio automatico delle fatture tramite SDI</p>
        </div>
        <Switch checked={value.abilitato} onCheckedChange={(v) => patch({ abilitato: v })} />
      </div>

      <div>
        <Label className="text-xs text-slate-500">Provider di Fatturazione</Label>
        <Select value={value.provider || ""} onValueChange={(v) => patch({ provider: v })}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Seleziona provider" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fatture_in_cloud">Fatture in Cloud</SelectItem>
            <SelectItem value="aruba">Aruba</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {value.provider === "fatture_in_cloud" && (
        <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800">
          <FileText className="w-4 h-4 flex-shrink-0" />
          <span>
            Non sai dove trovare Access Token e Company ID?{" "}
            <a
              href="/documents/GUIDA_FATTURE_IN_CLOUD.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline hover:text-teal-600"
            >
              Consulta la guida passo passo
            </a>
          </span>
        </div>
      )}

      {value.provider && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">
              {value.provider === "fatture_in_cloud" ? "Access Token" : "Username"}
            </Label>
            <Input
              type="text"
              value={value.api_key}
              onChange={(e) => patch({ api_key: e.target.value })}
              className="mt-1 font-mono text-sm"
              placeholder={value.provider === "fatture_in_cloud" ? "Token API" : "Username Aruba"}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">
              {value.provider === "fatture_in_cloud" ? "Company ID" : "Password"}
            </Label>
            <Input
              type={value.provider === "aruba" ? "password" : "text"}
              value={value.provider === "fatture_in_cloud" ? value.api_uid || "" : value.api_secret || ""}
              onChange={(e) =>
                patch({
                  [value.provider === "fatture_in_cloud" ? "api_uid" : "api_secret"]: e.target.value,
                })
              }
              className="mt-1 font-mono text-sm"
              placeholder={value.provider === "fatture_in_cloud" ? "ID Azienda" : "••••••••"}
            />
          </div>
        </div>
      )}

      {value.provider === "aruba" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Credential (firma automatica)</Label>
            <Input
              type="text"
              value={value.aruba_credential || ""}
              onChange={(e) => patch({ aruba_credential: e.target.value })}
              className="mt-1 font-mono text-sm"
              placeholder="Credenziale firma automatica"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Domain (firma automatica)</Label>
            <Input
              type="text"
              value={value.aruba_domain || ""}
              onChange={(e) => patch({ aruba_domain: e.target.value })}
              className="mt-1 font-mono text-sm"
              placeholder="Dominio firma automatica"
            />
          </div>
        </div>
      )}

      {value.provider && (
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={onTest}
          disabled={testing}
          className="border-emerald-200 text-emerald-700"
        >
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
          Test Connessione
        </Button>
      )}
    </div>
  );
});

const FattDatiAziendaliFields = memo(function FattDatiAziendaliFields({ value, setValue }) {
  const patch = (p) => setValue((prev) => ({ ...prev, ...p }));
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-slate-700">Dati Aziendali (Cedente/Prestatore)</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500">Ragione Sociale</Label>
          <Input
            value={value.ragione_sociale}
            onChange={(e) => patch({ ragione_sociale: e.target.value })}
            className="mt-1"
            placeholder="RSA San Giovanni S.r.l."
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Partita IVA</Label>
          <Input
            value={value.partita_iva}
            onChange={(e) => patch({ partita_iva: e.target.value })}
            className="mt-1 font-mono"
            placeholder="01234567890"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Codice Fiscale</Label>
          <Input
            value={value.codice_fiscale}
            onChange={(e) => patch({ codice_fiscale: e.target.value })}
            className="mt-1 font-mono"
            placeholder="01234567890"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs text-slate-500">Indirizzo</Label>
          <Input
            value={value.indirizzo}
            onChange={(e) => patch({ indirizzo: e.target.value })}
            className="mt-1"
            placeholder="Via Roma 1"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">CAP</Label>
          <Input
            value={value.cap}
            onChange={(e) => patch({ cap: e.target.value })}
            className="mt-1 font-mono"
            placeholder="00100"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Città</Label>
          <Input
            value={value.citta}
            onChange={(e) => patch({ citta: e.target.value })}
            className="mt-1"
            placeholder="Roma"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Provincia</Label>
          <Input
            value={value.provincia}
            onChange={(e) => patch({ provincia: e.target.value })}
            className="mt-1 font-mono"
            maxLength={2}
            placeholder="RM"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Regime Fiscale</Label>
          <Select value={value.regime_fiscale} onValueChange={(v) => patch({ regime_fiscale: v })}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RF01">RF01 - Ordinario</SelectItem>
              <SelectItem value="RF02">RF02 - Contribuenti minimi</SelectItem>
              <SelectItem value="RF04">RF04 - Agricoltura</SelectItem>
              <SelectItem value="RF19">RF19 - Forfettario</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
});

const FattRecapitiFields = memo(function FattRecapitiFields({ value, setValue }) {
  const patch = (p) => setValue((prev) => ({ ...prev, ...p }));
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-slate-700">Recapiti Elettronici e Banca</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">PEC</Label>
          <Input
            type="email"
            value={value.pec}
            onChange={(e) => patch({ pec: e.target.value })}
            className="mt-1"
            placeholder="pec@struttura.it"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Codice SDI</Label>
          <Input
            value={value.codice_sdi}
            onChange={(e) => patch({ codice_sdi: e.target.value })}
            className="mt-1 font-mono"
            placeholder="0000000"
            maxLength={7}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">IBAN</Label>
          <Input
            value={value.iban}
            onChange={(e) => patch({ iban: e.target.value })}
            className="mt-1 font-mono"
            placeholder="IT60X0542811101000000123456"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Banca</Label>
          <Input
            value={value.banca}
            onChange={(e) => patch({ banca: e.target.value })}
            className="mt-1"
            placeholder="Nome banca"
          />
        </div>
      </div>
    </div>
  );
});

const FattIvaFields = memo(function FattIvaFields({ value, setValue }) {
  const patch = (p) => setValue((prev) => ({ ...prev, ...p }));
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-slate-700">Regimi IVA Predefiniti e Numerazione</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-slate-500">IVA Retta Mensile</Label>
          <Select
            value={value.regime_iva_default_retta}
            onValueChange={(v) => patch({ regime_iva_default_retta: v })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="esente_art10">Esente Art.10</SelectItem>
              <SelectItem value="iva_4">IVA 4%</SelectItem>
              <SelectItem value="iva_22">IVA 22%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">IVA Servizi</Label>
          <Select
            value={value.regime_iva_default_servizi}
            onValueChange={(v) => patch({ regime_iva_default_servizi: v })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="iva_4">IVA 4%</SelectItem>
              <SelectItem value="iva_22">IVA 22%</SelectItem>
              <SelectItem value="esente_art10">Esente Art.10</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">IVA Rimborsi</Label>
          <Select
            value={value.regime_iva_default_rimborsi}
            onValueChange={(v) => patch({ regime_iva_default_rimborsi: v })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="esclusa_art15">Escl. Art.15</SelectItem>
              <SelectItem value="esente_art10">Esente Art.10</SelectItem>
              <SelectItem value="iva_4">IVA 4%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Prefisso Numerazione</Label>
          <Input
            value={value.numerazione_prefix}
            onChange={(e) => patch({ numerazione_prefix: e.target.value })}
            className="mt-1 font-mono"
            placeholder="FT"
            maxLength={5}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Prefisso Note di credito</Label>
          <Input
            value={value.numerazione_prefix_note_credito || "NC"}
            onChange={(e) => patch({ numerazione_prefix_note_credito: e.target.value })}
            className="mt-1 font-mono"
            placeholder="NC"
            maxLength={5}
          />
        </div>
      </div>
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
        <p>
          <strong>Esente Art.10</strong> — Prestazioni sanitarie e socio-assistenziali (DPR 633/72 Art.10)
        </p>
        <p>
          <strong>IVA 4%</strong> — Beni di prima necessità, ausili
        </p>
        <p>
          <strong>IVA 22%</strong> — Servizi accessori (parrucchiere, podologo, ecc.)
        </p>
        <p>
          <strong>Escl. Art.15</strong> — Rimborsi spese in nome e per conto (farmaci, ecc.)
        </p>
        <p className="mt-1">
          <strong>Bollo:</strong> non è addebitato sul totale della fattura né al destinatario
        </p>
      </div>
    </div>
  );
});

/**
 * Sezione fatturazione isolata dalla pagina Impostazioni.
 * Form spezzato in 4 stati + sotto-componenti memo: nessun controllo per carattere verso il server;
 * la lentezza era dovuta al re-render di tutti i Select Radix a ogni tasto.
 */
function FatturazioneElettronicaImpostazioni({ token, orgStrutture = [] }) {
  const { activeStruttura } = useAuth();
  const [fatturazioneConfig, setFatturazioneConfig] = useState({});
  const [loadingFattConfig, setLoadingFattConfig] = useState(false);
  const [savingFattConfig, setSavingFattConfig] = useState(false);
  const [testingFattConnection, setTestingFattConnection] = useState(false);
  const [showFattSection, setShowFattSection] = useState(false);
  const [fattConfigScope, setFattConfigScope] = useState(null);
  const [fattIsGlobal, setFattIsGlobal] = useState(true);
  const [copyingFattConfig, setCopyingFattConfig] = useState(false);
  const fetchGenRef = useRef(0);

  const [fattProvider, setFattProvider] = useState(FATT_PROVIDER_DEFAULTS);
  const [fattDati, setFattDati] = useState(FATT_DATI_DEFAULTS);
  const [fattRecapiti, setFattRecapiti] = useState(FATT_RECAPITI_DEFAULTS);
  const [fattIva, setFattIva] = useState(FATT_IVA_DEFAULTS);
  const [webhook, setWebhook] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const multiStruttura = orgStrutture.length > 1;
  const mergedFormRef = useRef({});
  useLayoutEffect(() => {
    mergedFormRef.current = { ...fattProvider, ...fattDati, ...fattRecapiti, ...fattIva };
  }, [fattProvider, fattDati, fattRecapiti, fattIva]);

  const fetchFatturazioneConfig = useCallback(async (strutturaId = null) => {
    const gen = ++fetchGenRef.current;
    setLoadingFattConfig(true);
    try {
      const params = strutturaId ? { strutturaId } : {};
      const res = await axios.get(`${API_URL}/api/fatturazione/config`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      if (gen !== fetchGenRef.current) return;
      const cfg = res.data.config || {};
      setFatturazioneConfig(cfg);
      setFattIsGlobal(res.data._isGlobal !== false);
      const parts = splitConfigFromApi(cfg);
      setFattProvider(parts.provider);
      setFattDati(parts.dati);
      setFattRecapiti(parts.recapiti);
      setFattIva(parts.iva);
    } catch {
      /* silent */
    } finally {
      if (gen === fetchGenRef.current) setLoadingFattConfig(false);
    }
  }, [token]);

  const orgStruttureKey = orgStrutture.map((s) => s.id || s.zohoRecordId || "").join(",");

  // Allinea l'ambito alla struttura attiva (switcher header), non alla sola globale.
  useEffect(() => {
    if (!token) return;
    const scope = multiStruttura
      ? resolveStrutturaScopeId(activeStruttura, orgStrutture)
      : null;
    setFattConfigScope(scope);
    fetchFatturazioneConfig(scope);
    // orgStrutture letto via key stabile; evita reset continui su nuova reference array
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orgStruttureKey
  }, [
    token,
    multiStruttura,
    activeStruttura?.id,
    activeStruttura?.zohoRecordId,
    orgStruttureKey,
    fetchFatturazioneConfig,
  ]);

  const handleSaveFattConfig = useCallback(async () => {
    setSavingFattConfig(true);
    try {
      const payload = { ...mergedFormRef.current, strutturaId: fattConfigScope || null };
      await axios.put(`${API_URL}/api/fatturazione/config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(
        fattConfigScope
          ? "Configurazione salvata per questa struttura"
          : "Configurazione globale salvata"
      );
      await fetchFatturazioneConfig(fattConfigScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingFattConfig(false);
    }
  }, [token, fattConfigScope, fetchFatturazioneConfig]);

  const handleTestFattConnection = useCallback(async () => {
    setTestingFattConnection(true);
    try {
      const payload = { ...mergedFormRef.current, strutturaId: fattConfigScope || null };
      const res = await axios.post(`${API_URL}/api/fatturazione/test-connection`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success(res.data.message || "Connessione riuscita");
      } else {
        toast.error(res.data.message || "Connessione fallita");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel test di connessione");
    } finally {
      setTestingFattConnection(false);
    }
  }, [token, fattConfigScope]);

  const handleCopyFattConfigToStruttura = async () => {
    if (!fattConfigScope) return;
    setCopyingFattConfig(true);
    try {
      await axios.post(
        `${API_URL}/api/fatturazione/config/copy-to-struttura`,
        { strutturaId: fattConfigScope },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configurazione copiata alla struttura");
      await fetchFatturazioneConfig(fattConfigScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella copia della configurazione");
    } finally {
      setCopyingFattConfig(false);
    }
  };

  const handleResetFattConfigToGlobal = async () => {
    if (!fattConfigScope) return;
    setCopyingFattConfig(true);
    try {
      await axios.delete(`${API_URL}/api/fatturazione/config/struttura/${fattConfigScope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Configurazione struttura rimossa, verrà utilizzata quella globale");
      await fetchFatturazioneConfig(fattConfigScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel ripristino");
    } finally {
      setCopyingFattConfig(false);
    }
  };

  const handleFattScopeChange = (value) => {
    const newScope = value === "__global__" ? null : value;
    setFattConfigScope(newScope);
    fetchFatturazioneConfig(newScope);
  };

  const scopeStrutturaName =
    orgStrutture.find((s) => s.id === fattConfigScope || s.zohoRecordId === fattConfigScope)?.name ||
    "questa struttura";
  const usingGlobalFallback = Boolean(fattConfigScope && fattIsGlobal);

  const loadWebhook = useCallback(async () => {
    if (!token || fattProvider.provider !== "fatture_in_cloud") return;
    try {
      const res = await axios.get(`${API_URL}/api/fatturazione/fic/webhook-subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWebhook(res.data);
    } catch {
      setWebhook(null);
    }
  }, [token, fattProvider.provider]);

  useEffect(() => { loadWebhook(); }, [loadWebhook]);

  const manageWebhook = async (action) => {
    setWebhookLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (action === "activate") await axios.post(`${API_URL}/api/fatturazione/fic/webhook-subscription`, {}, { headers });
      if (action === "verify") await axios.post(`${API_URL}/api/fatturazione/fic/webhook-subscription/verifica`, {}, { headers });
      if (action === "delete") await axios.delete(`${API_URL}/api/fatturazione/fic/webhook-subscription`, { headers });
      toast.success(action === "delete" ? "Webhook disattivato" : "Webhook aggiornato");
      await loadWebhook();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operazione webhook non riuscita");
    } finally {
      setWebhookLoading(false);
    }
  };

  return (
    <Card className="card-base">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowFattSection(!showFattSection)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm md:text-base flex items-center gap-2">
            <Receipt className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
            Fatturazione Elettronica
            {fatturazioneConfig.abilitato && (
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Attiva</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {fatturazioneConfig.provider && (
              <Badge variant="outline" className="text-xs">
                {fatturazioneConfig.provider === "fatture_in_cloud" ? "Fatture in Cloud" : "Aruba"}
              </Badge>
            )}
            {showFattSection ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Configurazione provider, dati aziendali e regimi IVA per la fatturazione elettronica
        </p>
      </CardHeader>
      {showFattSection && (
        <CardContent className="space-y-6">
          {multiStruttura && (
            <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <Label className="font-medium text-sm flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Ambito configurazione
                  </Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    In fatturazione viene usata la config della struttura attiva; se non è personalizzata, vale la globale.
                  </p>
                </div>
                <Select value={fattConfigScope || "__global__"} onValueChange={handleFattScopeChange}>
                  <SelectTrigger className="w-full sm:w-64 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__global__">
                      <span className="flex items-center gap-2">
                        <Globe className="w-3.5 h-3.5" /> Configurazione Globale (Default)
                      </span>
                    </SelectItem>
                    {orgStrutture.map((s) => (
                      <SelectItem key={s.id} value={s.id || s.zohoRecordId}>
                        <span className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5" /> {s.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {fattConfigScope && (
                <div className="flex items-center gap-2 flex-wrap">
                  {fattIsGlobal ? (
                    <>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        <Globe className="w-3 h-3 mr-1" /> {scopeStrutturaName}: usa config globale
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7"
                        onClick={handleCopyFattConfigToStruttura}
                        disabled={copyingFattConfig || loadingFattConfig}
                      >
                        {copyingFattConfig ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Copy className="w-3 h-3 mr-1" />
                        )}
                        Personalizza per questa struttura
                      </Button>
                    </>
                  ) : (
                    <>
                      <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        <Building2 className="w-3 h-3 mr-1" /> Config personalizzata: {scopeStrutturaName}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={handleResetFattConfigToGlobal}
                        disabled={copyingFattConfig || loadingFattConfig}
                      >
                        {copyingFattConfig ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <RotateCcw className="w-3 h-3 mr-1" />
                        )}
                        Ripristina globale
                      </Button>
                    </>
                  )}
                </div>
              )}

              {usingGlobalFallback && (
                <p className="text-xs text-amber-800 bg-amber-50/80 rounded-md px-2.5 py-2 border border-amber-100">
                  Stai vedendo i dati FIC della configurazione <strong>globale</strong>.
                  Per una società diversa su {scopeStrutturaName}: clicca «Personalizza», modifica Company ID/token e salva.
                  Finché non personalizzi, in fatturazione userà sempre la società globale.
                </p>
              )}
            </div>
          )}

          {loadingFattConfig ? (
            <div className="text-center py-6">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            </div>
          ) : (
            <>
              <FattProviderFields
                value={fattProvider}
                setValue={setFattProvider}
                onTest={handleTestFattConnection}
                testing={testingFattConnection}
              />
              <FattDatiAziendaliFields value={fattDati} setValue={setFattDati} />
              <FattRecapitiFields value={fattRecapiti} setValue={setFattRecapiti} />
              <FattIvaFields value={fattIva} setValue={setFattIva} />
              {fattProvider.provider === "fatture_in_cloud" && (
                <div className="space-y-2 rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Notifiche automatiche (webhook)</p>
                      <p className="text-xs text-slate-500">Riduce il polling: Fatture in Cloud notifica aggiornamenti e stati SDI.</p>
                    </div>
                    <Badge variant="outline">{webhook?.active ? (webhook?.verified ? "Attivo" : "Da verificare") : "Non attivo"}</Badge>
                  </div>
                  {webhook?.sink && <p className="break-all text-xs text-slate-500">URL sink: {webhook.sink}</p>}
                  {webhook?.ultimo_evento_at && <p className="text-xs text-slate-500">Ultimo evento: {new Date(webhook.ultimo_evento_at).toLocaleString("it-IT")}</p>}
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={webhookLoading} onClick={() => manageWebhook("activate")}>Attiva</Button>
                    {webhook?.active && <Button type="button" size="sm" variant="outline" disabled={webhookLoading} onClick={() => manageWebhook("verify")}>Verifica</Button>}
                    {webhook?.active && <Button type="button" size="sm" variant="destructive" disabled={webhookLoading} onClick={() => manageWebhook("delete")}>Disattiva</Button>}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  type="button"
                  onClick={handleSaveFattConfig}
                  disabled={savingFattConfig}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingFattConfig ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {fattConfigScope
                    ? usingGlobalFallback
                      ? `Salva per ${scopeStrutturaName}`
                      : "Salva configurazione struttura"
                    : "Salva configurazione globale"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default memo(FatturazioneElettronicaImpostazioni);
